import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import { fetchProviderById, fetchProviders, ProviderWithProducts } from "../../../hooks/useProviders";
import ProviderHeroSection from "./components/ProviderHeroSection";
import ProviderTabNav from "./components/ProviderTabNav";
import ProviderPhilosophySection from "./components/ProviderPhilosophySection";
import ProviderProductsSection from "./components/ProviderProductsSection";
import ProviderReviewSection from "./components/ProviderReviewSection";
import ProviderFaqSection from "./components/ProviderFaqSection";

export default function ProviderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState("philosophy");
  const [visible, setVisible] = useState(false);
  const [provider, setProvider] = useState<ProviderWithProducts | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatedProviders, setRelatedProviders] = useState<ProviderWithProducts[]>([]);

  useEffect(() => {
    setTimeout(() => setVisible(true), 80);
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setProvider(null);
    fetchProviderById(id).then((data) => {
      setProvider(data);
      setLoading(false);
    });
  }, [id]);

  useEffect(() => {
    if (!provider) return;
    fetchProviders().then((all) => {
      const related = all
        .filter((p) => p.id !== provider.id)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3);
      setRelatedProviders(related);
    });
  }, [provider]);

  // SEO: update meta tags based on provider data
  useEffect(() => {
    if (!provider) return;
    const displayName = provider.service_name || provider.name;
    const title = `${displayName} | TSUGITE 職人・専門家`;
    const description = provider.bio
      ? `${displayName}：${provider.bio.slice(0, 120)}。TSUGITEで動画講座・教材キットを購入して学べます。`
      : `${displayName}の職人・専門家ページ。TSUGITEで動画講座・教材キットを購入できます。`;

    document.title = title;

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", description);
    setMeta("keywords", `${displayName}, 職人, ${provider.category || "専門家"}, 動画講座, TSUGITE`);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:type", "profile", true);
    setMeta("og:url", `https://tsugite.jp/providers/${id}`, true);
    const ogImage = provider.cover_image_url || provider.avatar_url;
    if (ogImage) setMeta("og:image", ogImage, true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    // Schema.org Person/Organization
    let schemaEl = document.getElementById("schema-provider");
    if (!schemaEl) {
      schemaEl = document.createElement("script");
      schemaEl.setAttribute("type", "application/ld+json");
      schemaEl.id = "schema-provider";
      document.head.appendChild(schemaEl);
    }
    schemaEl.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ProfilePage",
      "name": title,
      "description": description,
      "url": `https://tsugite.jp/providers/${id}`,
      "image": ogImage || "",
      "mainEntity": {
        "@type": "Person",
        "name": provider.name,
        "jobTitle": provider.service_name || provider.category || "職人・専門家",
        "description": provider.bio || "",
        "address": provider.location ? { "@type": "PostalAddress", "addressLocality": provider.location } : undefined,
        "aggregateRating": provider.review_count && provider.review_count > 0 ? {
          "@type": "AggregateRating",
          "ratingValue": provider.rating,
          "reviewCount": provider.review_count,
        } : undefined,
      },
    });

    return () => {
      document.title = "TSUGITE | 職人・専門家から学ぶプラットフォーム";
      if (schemaEl) schemaEl.remove();
    };
  }, [provider, id]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    const el = document.getElementById(tabId);
    if (el) {
      const offset = 120;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory">
        <Navbar />
        <main className="pt-16">
          <div className="h-80 bg-stone-200 animate-pulse" />
          <div className="bg-white border-b border-stone-100 h-24 animate-pulse" />
          <div className="max-w-screen-xl mx-auto px-4 py-8 space-y-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-40 bg-stone-200 rounded-2xl animate-pulse" />
            ))}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-ivory">
        <Navbar />
        <main className="pt-32 text-center">
          <i className="ri-store-2-line text-6xl text-charcoal/20 block mb-4" />
          <p className="text-charcoal/50 font-sans">事業者が見つかりませんでした</p>
          <Link to="/providers" className="mt-4 inline-block text-teal font-sans hover:underline cursor-pointer">
            事業者一覧へ →
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-ivory transition-opacity duration-700 ${visible ? "opacity-100" : "opacity-0"}`}>
      <Navbar />
      <main className="pt-16">
        <ProviderHeroSection provider={provider} />
        <ProviderTabNav activeTab={activeTab} onTabChange={handleTabChange} />

        <div className="max-w-screen-xl mx-auto px-4 md:px-8">
          <ProviderPhilosophySection provider={provider} />
          <ProviderProductsSection provider={provider} />
          <ProviderReviewSection
            providerId={provider.id}
            totalRating={provider.rating ?? 0}
            reviewCount={provider.review_count ?? 0}
          />
          <ProviderFaqSection provider={provider} />

          {relatedProviders.length > 0 && (
            <section className="py-14 border-t border-stone-100">
              <div className="mb-6">
                <p className="text-gold text-xs font-display italic tracking-widest mb-1">Related</p>
                <h2 className="font-serif text-deep-green text-2xl font-light">他の事業者も見てみましょう</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedProviders.map((p) => {
                  const cover = p.cover_image_url ||
                    `https://public.readdy.ai/ai/img_res/329e16c1bfa8a12eecabe359647ae627.jpg${p.id.slice(0, 8)}&orientation=landscape`;
                  return (
                    <Link
                      key={p.id}
                      to={`/providers/${p.id}`}
                      className="group bg-white rounded-2xl overflow-hidden border border-stone-100 hover:-translate-y-1 transition-transform duration-300 cursor-pointer block"
                    >
                      <div className="h-36 overflow-hidden relative">
                        <img
                          src={cover}
                          alt={p.service_name ?? p.name}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-deep-green/50 to-transparent" />
                      </div>
                      <div className="p-4">
                        <p className="font-serif text-deep-green text-sm font-medium">{p.service_name || p.name}</p>
                        <p className="text-xs text-charcoal/50 font-sans mt-0.5">
                          {p.name}{p.location ? ` · ${p.location}` : ""}
                        </p>
                        <div className="flex items-center gap-1 mt-2 text-xs text-charcoal/50 font-sans">
                          <span className="text-gold">★</span>
                          <strong className="text-charcoal">{(p.rating ?? 0).toFixed(1)}</strong>
                          <span>({p.review_count ?? 0}件)</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
